import { useState } from 'react';
import { t } from '@app/i18n';
import { Alert, Button, copyText, LoadingButton, type CopyState } from '@zarya/ui';
import s from './Users.module.css';

export interface PasswordDialogProps {
  open: boolean;
  /** Nome de quem recebeu a senha, para o título. */
  usuario: string;
  senha: string;
  /** `criado` muda o texto: senha inicial × redefinição. */
  contexto: 'criado' | 'redefinida';
  onClose: () => void;
}

/**
 * Senha exibida **uma única vez** (FR-107, FR-109, R20).
 *
 * Não há reabrir: fechado o diálogo, a senha some da tela e da memória do
 * console. Quem perder tem de redefinir de novo — não existe autoatendimento.
 */
export function PasswordDialog({ open, usuario, senha, contexto, onClose }: PasswordDialogProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  if (!open) return null;

  async function copy() {
    setCopyState((await copyText(senha)) ? 'done' : 'failed');
  }

  return (
    <div className={s.overlay} data-testid="senha-overlay">
      <div className={s.mbox} role="dialog" aria-modal="true" aria-labelledby="senha-titulo">
        <div className={s.mh}>
          <h3 id="senha-titulo">
            {t(
              contexto === 'criado' ? 'users.password.title.created' : 'users.password.title.reset',
              { name: usuario },
            )}
          </h3>
        </div>

        <div className={s.mb}>
          <Alert tone="warning" title={t('users.password.warning.title')}>
            {t('users.password.warning.message', { name: usuario })}
          </Alert>

          <div className={s.senhaBox}>
            <p className={s.senhaValor} data-testid="senha-gerada">
              {senha}
            </p>
            <LoadingButton
              variant="out"
              size="sm"
              icon="csv"
              onClick={() => copy()}
              loadingLabel={t('users.password.copying')}
            >
              {copyState === 'failed'
                ? t('common.copyFailed')
                : t(copyState === 'done' ? 'users.password.copied' : 'users.password.copy')}
            </LoadingButton>
          </div>
        </div>

        <div className={s.mf}>
          <Button onClick={onClose}>{t('users.password.close')}</Button>
        </div>
      </div>
    </div>
  );
}
